// App.js
import { Berechnungserklärung } from "./describe";
import React, { useState } from "react";
import {
  Layout,
  Input,
  Button,
  Card,
  Typography,
  Select,
  Statistic,
  Form,
  Spin,
  Modal,
} from "antd";
import { AdminLayout } from "@opendash/core";

const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;

function App() {
  const [adresse, setAdresse] = useState("");
  const [dachflaeche, setDachflaeche] = useState("");
  const [dachtyp, setDachtyp] = useState("ziegeldach");
  const [niederschlag, setNiederschlag] = useState(null);
  const [wasserErtrag, setWasserErtrag] = useState(null);
  const [empfohleneZisternengroesse, setEmpfohleneZisternengroesse] =
    useState(null);
  const [loading, setLoading] = useState(false);
  const [monatsNiederschlag, setMonatsNiederschlag] = useState(0);

  const [visible, setVisible] = useState(false);

  const showModal = () => {
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const abflussbeiwertMap = {
    ziegeldach: 0.8,
    flachdach: 0.7,
    begruentes_dach: 0.5,
  };

  const handleDachtypChange = (value) => {
    setDachtyp(value);
  };

  const handleSubmit = async () => {
    if (!adresse) {
      alert("Bitte geben Sie Ihre Adresse ein.");
      return;
    }

    const dachflaecheNumber = parseFloat(dachflaeche);
    if (isNaN(dachflaecheNumber) || dachflaecheNumber <= 0) {
      alert("Bitte geben Sie eine gültige Dachfläche ein.");
      return;
    }

    setLoading(true);

    try {
      // Geokodierung der Adresse mit Nominatim
      const geoResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          adresse
        )}&format=json&addressdetails=1&limit=1`
      );
      const geoData = await geoResponse.json();

      if (geoData.length === 0) {
        alert("Adresse nicht gefunden. Bitte überprüfen Sie Ihre Eingabe.");
        setLoading(false);
        return;
      }

      const { lat, lon } = geoData[0];

      // Abrufen der monatlichen Niederschlagsdaten für den letzten Monat
      const today = new Date();
      const pastDates = new Date();
      pastDates.setMonth(today.getMonth() - 1);

      const startDate = pastDates.toISOString().split("T")[0];
      const endDate = today.toISOString().split("T")[0];

      const weatherResponse = await fetch(
        `https://api.brightsky.dev/weather?lat=${lat}&lon=${lon}&date=${startDate}&last_date=${endDate}`
      );
      const weatherData = await weatherResponse.json();

      const wetterdaten = weatherData.weather;

      if (!wetterdaten || wetterdaten.length === 0) {
        alert("Keine Wetterdaten für diesen Ort verfügbar.");
        setLoading(false);
        return;
      }

      // Summe des Niederschlags für den letzten Monat berechnen
      const monatlicherNiederschlag = wetterdaten.reduce(
        (summe, datensatz) => summe + (datensatz.precipitation || 0),
        0
      );

      // In Litern speichern (1 mm = 1 Liter pro m²)
      setMonatsNiederschlag(monatlicherNiederschlag * dachflaecheNumber); // Liter

      // Abrufen der historischen Niederschlagsdaten für die letzten 10 Jahre
      const pastDate = new Date();
      pastDate.setFullYear(today.getFullYear() - 10);
      const startHistoricalDate = pastDate.toISOString().split("T")[0];
      const endHistoricalDate = today.toISOString().split("T")[0];

      const climateResponse = await fetch(
        `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${startHistoricalDate}&end_date=${endHistoricalDate}&daily=precipitation_sum&timezone=Europe/Berlin`
      );
      const climateData = await climateResponse.json();

      if (
        !climateData ||
        !climateData.daily ||
        !climateData.daily.precipitation_sum
      ) {
        alert("Keine Klimadaten für diesen Ort verfügbar.");
        setLoading(false);
        return;
      }

      // Summe des Niederschlags über 10 Jahre in mm
      const gesamteNiederschlagssumme =
        climateData.daily.precipitation_sum.reduce((acc, val) => acc + val, 0);

      // Durchschnittlicher jährlicher Niederschlag in mm
      const durchschnittlicherJahresniederschlag =
        gesamteNiederschlagssumme / 10; // mm/Jahr

      // Speichern des durchschnittlichen jährlichen Niederschlags
      setNiederschlag(durchschnittlicherJahresniederschlag); // mm/Jahr

      // Gesamtvolumen des Niederschlags auf der Dachfläche in Litern pro Jahr
      const gesamtniederschlagInLitern =
        durchschnittlicherJahresniederschlag * dachflaecheNumber; // Liter/Jahr

      // Berechnung des Wasserertrags
      const abflussbeiwert = abflussbeiwertMap[dachtyp];
      const wasserErtragBerechnet = gesamtniederschlagInLitern * abflussbeiwert; // Liter/Jahr
      setWasserErtrag(wasserErtragBerechnet);

      // Berechnung der empfohlenen Zisternengröße
      const speicherdauerInTagen = 21; // Anzahl der Tage
      const tagesertrag = wasserErtragBerechnet / 365; // Durchschnittlicher täglicher Ertrag in Litern
      const empfohleneZisternengroesseBerechnet =
        tagesertrag * speicherdauerInTagen; // in Litern
      setEmpfohleneZisternengroesse(empfohleneZisternengroesseBerechnet * 1.2);
    } catch (error) {
      console.error("Fehler beim Abrufen der Daten:", error);
      alert(
        "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout contentPadding>
      <div style={{ width: "750px", margin: "0 auto", textAlign: "center" }}>
        <Title level={2} style={{ textAlign: "center" }}>
          Regenwasserberechnung
        </Title>

        <Form layout="vertical">
          <Form.Item label="Adresse">
            <Input
              placeholder="Geben Sie Ihre Wohnort und ggf. Straßennamen ein"
              value={adresse}
              onChange={(e) => setAdresse(e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Dachfläche (m²)">
            <Input
              placeholder="Geben Sie Ihre Dachfläche in m² ein"
              value={dachflaeche}
              onChange={(e) => setDachflaeche(e.target.value)}
              type="number"
            />
          </Form.Item>

          <Form.Item label="Dachtyp">
            <Select
              value={dachtyp}
              onChange={handleDachtypChange}
              style={{ width: "100%" }}
            >
              <Option value="ziegeldach">Ziegeldach</Option>
              <Option value="flachdach">Flachdach</Option>
              <Option value="begruentes_dach">Begrüntes Dach</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              onClick={handleSubmit}
              style={{ width: "100%" }}
            >
              Berechnen
            </Button>
          </Form.Item>
        </Form>

        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 9999,
            }}
          >
            <Spin />
          </div>
        )}

        {wasserErtrag !== null && monatsNiederschlag > 0 && (
          <Card
            title="Ergebnisse"
            style={{
              marginTop: "20px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Statistic
                title="Niederschlag der letzten 4 Wochen"
                value={monatsNiederschlag.toLocaleString("de-DE")}
                suffix="Liter"
              />
              <Statistic
                title="Jährlicher Niederschlag"
                value={niederschlag.toLocaleString("de-DE")}
                suffix="Liter"
              />
              <br />
              <Statistic
                title="Möglicher Wasserertrag pro Jahr"
                value={wasserErtrag.toLocaleString("de-DE")}
                suffix="Liter"
              />
              <Statistic
                title="Empfohlene Zisternengröße"
                value={empfohleneZisternengroesse.toLocaleString("de-DE")}
                suffix="Liter"
              />
            </div>
          </Card>
        )}

        <Button
          type="primary"
          shape="circle"
          icon={<span>?</span>}
          size="large"
          onClick={showModal}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1000,
          }}
        />

        <Modal
          title="Berechnungserklärung"
          visible={visible}
          onCancel={handleCancel}
          footer={null}
        >
          <Berechnungserklärung />
        </Modal>
      </div>
    </AdminLayout>
  );
}

export default App;
